import dayjs from "dayjs";
import { WIPCampaignMetrics } from "../../../store/apis/wipApi";
import type { ManualMetricField } from "./columns";
import { parseLocaleNumber } from "../../../utils/numberFormat";

/** Safe division that returns 0 when denominator is 0 or non-finite. */
function safeDiv(numerator: number, denominator: number): number {
  if (denominator === 0 || !Number.isFinite(denominator)) return 0;
  const result = numerator / denominator;
  return Number.isFinite(result) ? result : 0;
}

/**
 * Derived fields computed from WIPCampaignMetrics.
 * These are pre-computed before rendering so that both cell rendering
 * and CSV export share the same formula without duplication.
 */
export interface DerivedFields {
  /** expectedVolumeUntilYesterday / targetVolume (0–1) */
  expectedVolumePercent: number;
  /** accumulatedVolumeUntilYesterday / targetVolume (0–1) */
  obtainedVolumePercent: number;
  /** accumulatedVolumeUntilYesterday − expectedVolumeUntilYesterday */
  volumeVariation: number;
  /** volumeVariation / expectedVolumeUntilYesterday (signed ratio) */
  volumeVariationPercent: number;
  /** Inversión de plataforma actual (la del margen guardado hoy) que tocaba
   *  llevar gastada a cierre de ayer con un reparto lineal:
   *  currentPlatformInversion × días transcurridos / días totales (sin pasar
   *  del 100% una vez acabada la campaña). */
  expectedPlatformInvestmentUntilYesterday: number;
  /** totalPlatformCost / currentPlatformInversion. El pacingInvestmentPercent
   *  del servidor va sobre la inversión inicial. */
  currentPacingInvestmentPercent: number;
  /** Inversión plataforma que deja el margen ideal: totalInversion × (1 − margen)
   *  − costes añadidos.
   *  Sin tocar coincide con investmentNeededTotalReal; con un borrador, es la
   *  que se enviaría al aceptar. null sin margen ideal. */
  idealInvestment: number | null;
  /** viewableImpressions / measurableImpressions (0–1). null sin medibles. */
  viewabilityRate: number | null;
  /** engagements / impressions (0–1). null sin impresiones o sin interacciones. */
  engagementRate: number | null;
  /** Costes unitarios reales sobre el coste de cliente acumulado, sea cual sea
   *  el objetivo de la línea. null sin unidades de esa métrica. */
  cpmReal: number | null;
  cpcReal: number | null;
  cpvReal: number | null;
  cpaReal: number | null;
  cpeReal: number | null;
  /** Volumen con el que acabaría la línea si sigue al ritmo que lleva (ver
   *  projectVolume). null sin fechas, sin volumen previsto, sin empezar o con
   *  ayer a 0. */
  projectedVolume: number | null;
  /** projectedVolume / targetVolume − 1 (signed ratio). null sin proyección. */
  projectionVariation: number | null;
  /** En vuelo y ayer no llegó nada: o está parada o aún no se ha sincronizado,
   *  así que en vez de proyectar se avisa ("Datos a 0"). */
  projectionZeroData: boolean;
}

/** Volumen de ayer en la unidad del objetivo, igual que el acumulado del
 *  servidor: CPE cuenta interacciones si la plataforma las da, si no impresiones. */
const yesterdayVolume = (r: WIPCampaignMetrics): number => {
  switch (r.objective) {
    case "CPC":
      return r.yesterdayClicks;
    case "CPV":
    case "CPMV":
      return r.yesterdayCompleteViews;
    case "CPA":
    case "CPL":
      return r.yesterdayConversions;
    case "CPE":
      return r.engagements > 0
        ? r.yesterdayEngagements
        : r.yesterdayImpressions;
    default:
      return r.yesterdayImpressions;
  }
};

export type VolumeProjection =
  | { kind: "none" }
  | { kind: "zeroData" }
  | { kind: "projected"; volume: number };

/**
 * Volumen al cierre de la línea si sigue al ritmo que lleva, igual que la
 * predicción de las gráficas: el acumulado hasta ayer más, cada día que queda,
 * lo mismo que entregó ayer. Si ayer no llegó nada (parada o sin sincronizar)
 * no se proyecta: se avisa. Una línea finalizada proyecta lo que entregó.
 */
export function projectVolume(r: WIPCampaignMetrics): VolumeProjection {
  if (r.targetVolume <= 0 || r.totalDays <= 0) return { kind: "none" };
  // Días cerrados hasta ayer, los mismos que reparte el volumen esperado.
  const daysDone = Math.round(
    safeDiv(r.expectedVolumeUntilYesterday, r.targetVolume) * r.totalDays
  );
  if (daysDone <= 0) return { kind: "none" };
  const daysLeft = Math.max(0, r.totalDays - daysDone);
  if (daysLeft === 0)
    return { kind: "projected", volume: r.accumulatedVolumeUntilYesterday };
  const yesterday = yesterdayVolume(r);
  if (yesterday <= 0) return { kind: "zeroData" };
  return {
    kind: "projected",
    volume: r.accumulatedVolumeUntilYesterday + yesterday * daysLeft,
  };
}

/** Ratio that stays null (→ "—") when there is nothing to divide by. */
const ratioOrNull = (numerator: number, denominator: number): number | null =>
  denominator > 0 && Number.isFinite(numerator)
    ? numerator / denominator
    : null;

export type EnrichedRow = WIPCampaignMetrics & DerivedFields;

export function enrichRow(r: WIPCampaignMetrics): EnrichedRow {
  const volumeVariation =
    r.accumulatedVolumeUntilYesterday - r.expectedVolumeUntilYesterday;
  const projection = projectVolume(r);
  const projectedVolume =
    projection.kind === "projected" ? projection.volume : null;
  return {
    ...r,
    expectedVolumePercent: safeDiv(
      r.expectedVolumeUntilYesterday,
      r.targetVolume
    ),
    obtainedVolumePercent: safeDiv(
      r.accumulatedVolumeUntilYesterday,
      r.targetVolume
    ),
    volumeVariation,
    volumeVariationPercent: safeDiv(
      volumeVariation,
      r.expectedVolumeUntilYesterday
    ),
    expectedPlatformInvestmentUntilYesterday:
      r.currentPlatformInversion *
      safeDiv(Math.min(r.elapsedDays, r.totalDays), r.totalDays),
    currentPacingInvestmentPercent: safeDiv(
      r.totalPlatformCost,
      r.currentPlatformInversion
    ),
    idealInvestment:
      r.marginIdeal == null ? null : investmentForMargin(r, r.marginIdeal),
    viewabilityRate: ratioOrNull(
      r.viewableImpressions,
      r.measurableImpressions
    ),
    engagementRate:
      r.engagements > 0 ? ratioOrNull(r.engagements, r.impressions) : null,
    cpmReal: ratioOrNull(r.accumulatedClientCost * 1000, r.impressions),
    cpcReal: ratioOrNull(r.accumulatedClientCost, r.clicks),
    cpvReal: ratioOrNull(r.accumulatedClientCost, r.completeViews),
    cpaReal: ratioOrNull(r.accumulatedClientCost, r.conversions),
    cpeReal: ratioOrNull(r.accumulatedClientCost, r.engagements),
    projectedVolume,
    projectionVariation:
      projectedVolume === null ? null : projectedVolume / r.targetVolume - 1,
    projectionZeroData: projection.kind === "zeroData",
  };
}

/** A margin draft is valid when it is a 0–100 number ("12,5" and "12.5" both work). */
export function parseMarginDraft(draft: string): number | null {
  const { status, value } = parseLocaleNumber(draft);
  if (status === "empty" || status === "invalid" || value === null) return null;
  return value >= 0 && value <= 100 ? value : null;
}

/** True when the row's margin can be changed from tomorrow on (from today on its last day). */
export const isMarginEditable = (r: WIPCampaignMetrics): boolean =>
  r.marginForecast?.fromDate != null;

/** When a margin accepted now applies: "desde hoy" (last day), "desde mañana" or a date. */
export function marginFromText(
  r: Pick<WIPCampaignMetrics, "marginForecast">
): string {
  const from = r.marginForecast?.fromDate;
  if (!from) return "";
  if (from === dayjs().format("YYYY-MM-DD")) return "desde hoy";
  if (from === dayjs().add(1, "day").format("YYYY-MM-DD"))
    return "desde mañana";
  return `desde el ${dayjs(from).format("DD/MM/YYYY")}`;
}

/** Platform investment a margin leaves on the client investment once the
 *  added costs (adserver, trackers…) are out, like the brief's margin. */
export const investmentForMargin = (
  r: Pick<WIPCampaignMetrics, "totalInversion" | "addedCostsTotal">,
  margin: number
): number => r.totalInversion * (1 - margin / 100) - r.addedCostsTotal;

/** The row with an edited ideal margin: the investment to accept follows it. */
export function applyMarginDraft(
  row: EnrichedRow,
  margin: number
): EnrichedRow {
  return {
    ...row,
    marginIdeal: margin,
    idealInvestment: investmentForMargin(row, margin),
  };
}

/** Mensaje de error que devuelve el backend en una petición fallida, si lo hay. */
export const getErrorMessage = (error: unknown): string | null => {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: { message?: string } }).data;
    if (data && typeof data.message === "string") return data.message;
  }
  return null;
};

/**
 * Parses a manual metric draft. Empty clears the value (null = "sin dato");
 * counts must be whole numbers, the investment may carry decimals ("1.234,5"
 * and "1234.5" both work). undefined = not a valid value.
 */
export function parseManualDraft(
  field: ManualMetricField,
  draft: string
): number | null | undefined {
  if (draft.trim() === "") return null;
  const parsed = parseLocaleNumber(draft);
  if (parsed.status === "empty" || parsed.status === "invalid")
    return undefined;
  if (parsed.value === null) return undefined;
  let n = parsed.value;
  if (parsed.status === "ambiguous") {
    // "1.234": en un contador es mil doscientos treinta y cuatro; en un importe
    // no se adivina (queda en rojo) para no guardar 1,234 por 1.234.
    if (field === "platformInversion") return undefined;
    n = Number(parsed.canonical.replace(".", ""));
  }
  if (n < 0) return undefined;
  if (field !== "platformInversion" && !Number.isInteger(n)) return undefined;
  return n;
}
